import React, { type ReactNode, useId, useState } from 'react';

import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { PromoFeatureKey, type PromoFeatureRoleContext } from '@luxuryescapes/contract-svc-promo';

import { usePromoFeaturesConfig } from '~/hooks/usePromoFeatureAvailability';

import isProd from '~/utils/isProd';

import PromoFeatureAccessChips from './PromoFeatureAccessChips';
import PromoFeatureHeader from './PromoFeatureHeader';
import { findPromoFeatureIndicator } from './PromoTester/promoFeatureIndicators';
import { getPromoFeatureDisabledReason } from './promoFeatureDisabledReason';

export interface PromoFeatureRadioOption {
  value: string;
  label: string;
  description?: string;
  /** Optional client/config mapping. Multiple keys allow access when any feature is available. */
  featureKey?: PromoFeatureKey;
  featureKeys?: ReadonlyArray<PromoFeatureKey>;
  disabled?: boolean;
  disabledReason?: string;
}

interface SelectionProps {
  presentation: 'radioSelect' | 'select';
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<PromoFeatureRadioOption>;
  disabled?: boolean;
  disabledReason?: string;
  authRoles?: PromoFeatureRoleContext['authRoles'];
  promoRoles?: PromoFeatureRoleContext['promoRoles'];
}

export default function PromoFeature(props: Props | SelectionProps) {
  return 'options' in props ? <PromoFeatureSelection {...props} /> : <PromoFeatureSurface {...props} />;
}

function PromoFeatureSelection({
  presentation,
  label,
  value,
  onChange,
  options,
  disabled,
  disabledReason,
  authRoles,
  promoRoles,
}: SelectionProps) {
  const id = useId();
  const { config } = usePromoFeaturesConfig({ authRoles, promoRoles });
  const availableOptions = options.map((option) => {
    const keys = option.featureKeys ?? (option.featureKey ? [option.featureKey] : []);
    const reasons = keys.map((key) =>
      getPromoFeatureDisabledReason(findPromoFeatureIndicator(config.features, key), isProd()),
    );
    const hasAccess = keys.length === 0 || reasons.some((reason) => reason === undefined);
    const accessMessage = reasons.flatMap((reason) => (reason ? [reason.message] : [])).join(' Alternatively, ');
    const reason = disabled
      ? { kind: 'paused', message: disabledReason ?? 'This control is temporarily unavailable.' }
      : option.disabled
        ? { kind: 'paused', message: option.disabledReason ?? 'This option is temporarily unavailable.' }
        : !hasAccess
          ? {
              kind: reasons.some((entry) => entry?.kind === 'unknown') ? 'unknown' : reasons[0]?.kind,
              message: accessMessage,
            }
          : undefined;
    return { ...option, disabled: Boolean(reason), reason };
  });
  if (presentation === 'select') {
    return (
      <TextField
        select
        size="small"
        label={label}
        value={value}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onChange={(event) => {
          const next = event.target.value;
          if (availableOptions.some((option) => option.value === next && !option.disabled)) {
            onChange(next);
          }
        }}
        SelectProps={{
          renderValue: (selected) => availableOptions.find((option) => option.value === selected)?.label,
        }}
        sx={{ minWidth: 160 }}
      >
        {availableOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
            <Box>
              <Typography variant="body2">{option.label}</Typography>
              {(option.reason?.message || option.description) && (
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                  {option.reason?.message ?? option.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <FormControl disabled={disabled}>
      <FormLabel id={id} sx={{ typography: 'caption' }}>
        {label}
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={id}
        value={value}
        onChange={(_, next) => {
          if (availableOptions.some((option) => option.value === next && !option.disabled)) {
            onChange(next);
          }
        }}
      >
        {availableOptions.map((option) => {
          const reasonId = `${id}-${option.value}-reason`;
          const AccessIcon =
            option.reason?.kind === 'unknown'
              ? HelpOutlineIcon
              : option.reason?.kind === 'environment'
                ? VisibilityOffOutlinedIcon
                : option.reason?.kind === 'paused'
                  ? PauseCircleOutlineIcon
                  : LockOutlinedIcon;
          return (
            <Stack key={option.value} direction="row" alignItems="center" spacing={0.5} sx={{ mr: 2 }}>
              <Tooltip title={option.description ?? ''}>
                <FormControlLabel
                  value={option.value}
                  disabled={option.disabled}
                  control={
                    <Radio
                      size="small"
                      sx={{ py: 0.5 }}
                      inputProps={{ 'aria-describedby': option.reason ? reasonId : undefined }}
                    />
                  }
                  label={option.label}
                  slotProps={{ typography: { variant: 'body2' } }}
                  sx={{ mr: 0 }}
                />
              </Tooltip>
              {option.reason && (
                <Box id={reasonId} sx={{ display: 'none' }}>
                  {option.reason.message}
                </Box>
              )}
              {option.reason && (
                <Tooltip title={option.reason.message}>
                  <Box
                    component="span"
                    tabIndex={0}
                    aria-label={option.reason.message}
                    sx={{
                      display: 'inline-flex',
                      color: option.reason.kind === 'unknown' ? 'warning.main' : 'text.secondary',
                    }}
                  >
                    <AccessIcon fontSize="small" />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          );
        })}
      </RadioGroup>
    </FormControl>
  );
}

export type PromoFeaturePresentation = 'box' | 'accordion' | 'raw' | 'icon-expand' | 'chip';

interface Props {
  /** A contract feature key or an Admin Portal-only feature key. */
  featureKey: PromoFeatureKey;
  /** Defaults to the signed-in user's auth roles when omitted. */
  authRoles?: PromoFeatureRoleContext['authRoles'];
  /** Pass loaded promo roles; omit while their source is unresolved. */
  promoRoles?: PromoFeatureRoleContext['promoRoles'];
  /** `raw` has no outer surface; `chip` outlines controls with compact access badges and no full header. */
  presentation?: PromoFeaturePresentation;
  /** Accordion presentations start closed unless explicitly expanded. */
  defaultExpanded?: boolean;
  /** Icon and accessible label for the collapsed icon-expand control. */
  icon?: ReactNode;
  expandLabel?: string;
  /** Denied or unresolved access never renders children. Defaults to hiding the feature. */
  accessMode?: 'hide' | 'show-warning';
  fallbackTitle?: string;
  children: ReactNode;
}

type ConfigurationItem = {
  icon: ReactNode;
  label: string;
};

function getConfigurationItems(
  feature: NonNullable<ReturnType<typeof findPromoFeatureIndicator>>,
): ReadonlyArray<ConfigurationItem> {
  const isExperimental = feature.docType !== 'feature';
  const requiredRoleNames = feature.requiredRoles?.roles
    .filter((requirement) => requirement.type === 'auth_role' || requirement.type === 'promo_role')
    .map((requirement) => requirement.key);
  const roleLabel = requiredRoleNames?.join(feature.requiredRoles?.operator === 'and' ? ' and ' : ' or ');

  return [
    ...(roleLabel
      ? [
          {
            icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
            label: roleLabel,
          },
        ]
      : []),
    ...(isExperimental
      ? [
          {
            icon: <ScienceOutlinedIcon fontSize="small" />,
            label: 'Experimental',
          },
        ]
      : []),
    ...(feature.isOnlyTest
      ? [
          {
            icon: <VisibilityOffOutlinedIcon fontSize="small" />,
            label: 'Test only',
          },
        ]
      : []),
    ...(feature.isFunctionallyComplete
      ? [
          {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            label: 'Complete',
          },
        ]
      : []),
  ];
}

/** Enforces contract access and environment availability before rendering feature controls. */
function PromoFeatureSurface({
  featureKey,
  authRoles,
  promoRoles,
  presentation = 'box',
  defaultExpanded = false,
  accessMode = 'hide',
  fallbackTitle,
  icon = <AutoFixHighIcon />,
  expandLabel,
  children,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const { config } = usePromoFeaturesConfig({ authRoles, promoRoles });
  const feature = findPromoFeatureIndicator(config.features, featureKey);

  const isUnavailableInEnvironment = feature?.isOnlyTest && isProd();

  // Fail closed while roles are unresolved or the installed catalogue lacks this feature.
  if (feature?.canUse !== true || isUnavailableInEnvironment) {
    if (accessMode === 'hide') {
      return null;
    }

    const featureTitle = feature?.displayName ?? fallbackTitle ?? featureKey;
    const reason = getPromoFeatureDisabledReason(feature, isProd());

    return <Alert severity="warning">{feature ? reason?.message : `${featureTitle}: ${reason?.message}`}</Alert>;
  }

  const configurationItems = getConfigurationItems(feature);
  const header = <PromoFeatureHeader feature={feature} configurationItems={configurationItems} />;

  if (presentation === 'chip') {
    return (
      <Stack spacing={1} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <PromoFeatureAccessChips feature={feature} />
        {children}
      </Stack>
    );
  }

  if (presentation === 'raw') {
    return (
      <Stack spacing={2}>
        {header}
        {children}
      </Stack>
    );
  }

  if (presentation === 'icon-expand') {
    const label = expandLabel ?? feature.displayName;
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={label}>
            <IconButton
              color="primary"
              aria-label={label}
              aria-expanded={isExpanded}
              aria-controls={contentId}
              onClick={() => setIsExpanded((expanded) => !expanded)}
            >
              {icon}
            </IconButton>
          </Tooltip>
          <PromoFeatureAccessChips feature={feature} />
        </Stack>
        <Collapse in={isExpanded} id={contentId}>
          {children}
        </Collapse>
      </Box>
    );
  }

  if (presentation === 'accordion') {
    return (
      <Accordion
        variant="outlined"
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
        disableGutters
        sx={{
          borderRadius: 1,
          width: isExpanded ? '100%' : 'fit-content',
          maxWidth: '100%',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{header}</AccordionSummary>
        <AccordionDetails sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body2">{feature.description}</Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.currentStatusHeadline}
              </Typography>
              {feature.notes && (
                <Typography variant="body2" color="text.secondary">
                  {feature.notes}
                </Typography>
              )}
            </Stack>
            {children}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        width: '100%',
        borderColor: theme.palette.mode === 'dark' ? theme.palette.action.disabled : theme.palette.divider,
      })}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          {header}
          <Divider />
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}
